import React, { useState, useEffect } from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import tw from 'twin.macro';

import {
  Button,
  Col,
  Container,
  Flex,
  Form,
  Grid,
  Overlay,
  Section,
  AnchorText,
  SanityBlockContent,
} from '../../common';
import { getIcon } from '../../../utils';

const StyledHeroHeadline = styled(motion.h1)`
  color: ${(props) => props.$headline};
  ${tw`mb-10`};
`;

const StyledHeroSubheadline = styled(motion.div)`
  color: ${(props) => props.$subheadline};
`;

const StyledTestimonial = styled(motion.div)`
  background-color: ${(props) => props.$bgColor};
  color: ${(props) => props.$color};
  svg {
    color: ${(props) => props.$svgColor};
  }
`;

const StyledH3 = styled(motion.h3)`
  color: ${(props) => props.$color} !important;
  :hover {
    color: ${(props) => props.$colorHover} !important;
  }
`;

const HeroTextOnly = ({ sanity, page }) => {
  const {
    _rawHeroSubheadline,
    heroBgImg,
    heroHeadline,
    heroTestimonial,
    heroServices,
    heroOverlayType,
    heroOverlayAngle,
    heroOverlayWidth,
    heroOverlayHeight,
    heroOverlayColor,
    heroAddCtaButtons,
    heroPrimaryCtaButtonLabel,
    heroPrimaryCtaButtonLink,
    heroSecondaryCtaButtonLabel,
    heroSecondaryCtaButtonLink,
  } = page;
  const { colors, logos } = sanity;

  const variants = {
    hidden: { opacity: 0, scale: 0.8, x: -100 },
    visible: { opacity: 1, scale: 1, x: 0 },
  };

  const controls = useAnimation();
  const { ref, inView } = useInView({ triggerOnce: true });

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
    if (!inView) {
      controls.start('hidden');
    }
  }, [controls, inView]);

  const isVideo = page?.heroBgType === 'video';
  const videoUrl = page?.heroBgVideo?.asset?.url;

  return (
    <Section
      ref={ref}
      padding="custom"
      idName="hero-testimonial"
      classes="relative flex flex-col place-content-center place-items-center pt-[10rem] lg:pt-[14rem] pb-[7rem]"
    >
      <div className="absolute inset-0">
        {isVideo ? (
          <>
            <div className="relative h-full w-full overflow-hidden">
              <video
                src={videoUrl}
                className="absolute left-0 top-0 h-full w-full translate-x-[-25%] scale-150 transform object-cover"
                autoPlay
                loop
                muted
              />
            </div>
            <Overlay
              bgColor={colors?.hero?.overlayVideo}
              bgColorHover={colors?.neutral?.dark}
              bgColorStart={colors?.neutral?.darker}
              bgColorMid={colors?.neutral?.dark}
              bgColorEnd={colors?.neutral?.default}
            />
          </>
        ) : (
          <>
            <GatsbyImage
              image={heroBgImg?.asset?.gatsbyImageData}
              alt={heroBgImg?.alt}
              className="h-full w-full object-cover"
            />
            <Overlay
              heroOverlayType={heroOverlayType}
              heroOverlayAngle={heroOverlayAngle}
              heroOverlayWidth={heroOverlayWidth}
              heroOverlayHeight={heroOverlayHeight}
              bgColor={colors?.hero?.overlayImage}
              bgColorHover={colors?.neutral?.dark}
              bgColorStart={colors?.neutral?.darker}
              bgColorMid={colors?.neutral?.dark}
              bgColorEnd={colors?.neutral?.default}
            />
          </>
        )}
      </div>
      <Grid classes="lg:grid-cols-1 gap-y-16 lg:gap-24 max-w-5xl mx-auto lg:mx-0">
        <Col
          padding="0 0.5rem"
          classes="order-1 lg:col-span-3 p-4 lg:p-0 justify-start relative space-y-8 mx-auto"
        >
          <StyledHeroHeadline
            className="text-center lg:text-center"
            $headline={colors?.neutral?.lighter}
          >
            <span className="drop-shadow-headline-lg">{heroHeadline}</span>
          </StyledHeroHeadline>
          <StyledHeroSubheadline
            className="mx-auto max-w-3xl space-y-7 text-center text-lg font-medium sm:text-lg md:text-lg lg:text-xl xl:text-xl 2xl:text-2xl"
            $subheadline={colors?.neutral?.lighter}
          >
            <SanityBlockContent sanity={sanity} blocks={_rawHeroSubheadline} />
          </StyledHeroSubheadline>
          {heroAddCtaButtons ? (
            <Flex classes="pt-2 gap-6 flex-col lg:flex-row justify-center">
              <Button
                sanity={sanity}
                label={heroPrimaryCtaButtonLabel}
                internalLink={heroPrimaryCtaButtonLink?.slug?.current}
              ></Button>
              <Button
                secondaryBtn={true}
                sanity={sanity}
                label={heroSecondaryCtaButtonLabel}
                internalLink={heroSecondaryCtaButtonLink?.slug?.current}
              ></Button>
            </Flex>
          ) : null}
        </Col>
      </Grid>
    </Section>
  );
};

export { HeroTextOnly };
